import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from '../api/axios';
import { useIdleTimer } from '../hooks/useIdleTimer';
import API_CONFIG from '../config/api';

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [autoLogoutTimeout, setAutoLogoutTimeout] = useState(30);

  const handleIdleLogout = () => {
    if (user) {
      logout();
      alert('You have been logged out due to inactivity.');
    }
  };

  useIdleTimer(handleIdleLogout, autoLogoutTimeout * 60 * 1000);

  const fetchAutoLogoutTimeout = async () => {
    try {
      const response = await axios.get('/api/settings/config');
      setAutoLogoutTimeout(response.data.autoLogoutTimeout || 30);
    } catch (error) {
      // Use default timeout if fetch fails
      setAutoLogoutTimeout(30);
    }
  };

  useEffect(() => {
    fetchAutoLogoutTimeout();
    
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');
    
    if (token && userData) {
      const user = JSON.parse(userData);
      
      // Clean up any blob URLs from stored user data
      if (user.avatarUrl && user.avatarUrl.startsWith('blob:')) {
        delete user.avatarUrl;
        localStorage.setItem('user', JSON.stringify(user));
      }
      
      // Set user with fallback permissions first (using dot notation)
      if (!user.permissions) {
        user.permissions = user.role === 'ADMIN' ? ['dashboard.read', 'members.read', 'vehicles.read', 'jobcards.read', 'customers.read', 'inventory.read'] : ['dashboard.read'];
      }
      if (!user.roles) {
        user.roles = [{ slug: user.role?.toLowerCase() || 'user', name: user.role || 'User' }];
      }
      setUser(user);
      
      // Try to fetch RBAC permissions but don't block on failure
      axios.get(`/api/rbac/users/${user.id}/permissions`, {
        headers: { Authorization: `Bearer ${token}` }
      }).then(response => {
        user.permissions = response.data.permissions || user.permissions;
        user.roles = response.data.roles || user.roles;
        setUser(user);
        localStorage.setItem('user', JSON.stringify(user));
      }).catch(error => {
        // Don't logout on RBAC fetch failure - keep user logged in with role-based permissions
      }).finally(() => {
        setLoading(false);
      });
    } else {
      setLoading(false);
    }
  }, []);

  const login = async (email, password, companyCode) => {
    try {
      const payload = { email, password };
      if (companyCode) {
        payload.companyCode = companyCode;
      }
      
      const response = await axios.post('/api/auth/login', payload);
      const { user, token } = response.data;
      
      // Clean user data
      const cleanUser = { ...user };
      if (cleanUser.avatarUrl && cleanUser.avatarUrl.startsWith('blob:')) {
        delete cleanUser.avatarUrl;
      }
      
      // Set default permissions based on role (using dot notation)
      let defaultPermissions = ['dashboard.read'];
      if (cleanUser.role === 'ADMIN') {
        defaultPermissions = ['dashboard.read', 'members.read', 'vehicles.read', 'jobcards.read', 'customers.read', 'inventory.read'];
      } else if (cleanUser.role === 'MANAGER') {
        defaultPermissions = ['dashboard.read', 'reports.read'];
      }
      
      cleanUser.permissions = defaultPermissions;
      cleanUser.roles = [{ slug: cleanUser.role.toLowerCase(), name: cleanUser.role }];
      
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(cleanUser));
      setUser(cleanUser);
      
      setTimeout(() => {
        fetchPermissions(token, cleanUser).catch(() => {
          // Don't clear user state on permission fetch failure
        });
      }, 100);
      
      // Check for subscription warning
      if (response.data.subscriptionWarning) {
        const warning = response.data.subscriptionWarning;
        setTimeout(() => {
          if (warning.type === 'expired') {
            alert(`⚠️ SUBSCRIPTION EXPIRED\n\n${warning.message}`);
          } else if (warning.type === 'grace') {
            alert(`⏰ SUBSCRIPTION EXPIRING\n\n${warning.message}`);
          } else if (warning.type === 'expiring') {
            alert(`📋 Subscription Notice\n\n${warning.message}`);
          }
        }, 1000);
      }
      
      return { success: true };
    } catch (error) {
      
      if (error.code === 'ERR_NETWORK' || error.code === 'ECONNREFUSED') {
        return { success: false, error: `Cannot connect to server. Please ensure the server is running on ${API_CONFIG.baseURL}` };
      }
      
      if (error.response) {
        return { success: false, error: error.response.data?.error || `Server error: ${error.response.status}` };
      }
      
      return { success: false, error: error.message || 'Login failed' };
    }
  };

  const fetchPermissions = async (token, currentUser) => {
    try {
      const response = await axios.get(`/api/rbac/users/${currentUser.id}/permissions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const updatedUser = {
        ...currentUser,
        permissions: response.data.permissions || currentUser.permissions,
        roles: response.data.roles || currentUser.roles
      };
      
      localStorage.setItem('user', JSON.stringify(updatedUser));
      setUser(updatedUser);
    } catch (error) {
      // Don't logout on permission fetch failure - keep user logged in with role-based permissions
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
  };

  const updateUser = (userData) => {
    const updatedUser = { ...user, ...userData };
    setUser(updatedUser);
    localStorage.setItem('user', JSON.stringify(updatedUser));
  };

  const refreshUser = async () => {
    try {
      const profileResponse = await axios.get('/api/user/profile');
      let userData = { ...profileResponse.data };
      
      // Try to fetch permissions but don't fail if RBAC is not available
      try {
        const permissionsResponse = await axios.get(`/api/rbac/users/${user.id}/permissions`);
        userData.permissions = permissionsResponse.data.permissions || user.permissions;
        userData.roles = permissionsResponse.data.roles || user.roles;
      } catch (error) {
        userData.permissions = user.permissions;
        userData.roles = user.roles;
      }
      
      setUser(userData);
      localStorage.setItem('user', JSON.stringify(userData));
      return userData;
    } catch (error) {
      if (error.response?.status === 403 || error.response?.status === 401) {
        logout();
      }

      return null;
    }
  };
  
  const hasPermission = (permission) => {
    if (!user) return false;
    
    // Admin and Super Admin have full access (matching server-side wildcard '*' behavior)
    if (user.role === 'ADMIN' || user.role === 'SUPER_ADMIN') {
      return true;
    }
    
    // Check if user has super_admin role
    if (user.roles?.some(role => role.slug === 'super_admin')) {
      return true;
    }
    
    // Check wildcard permission
    if (user.permissions?.includes('*')) {
      return true;
    }
    
    // Check explicit permission
    return user.permissions?.includes(permission) || false;
  };
  
  const hasAnyPermission = (permissions) => {
    if (!user || !permissions?.length) return false;
    return permissions.some(permission => hasPermission(permission));
  };



  const value = {
    user,
    login,
    logout,
    updateUser,
    refreshUser,
    hasPermission,
    hasAnyPermission,
    loading
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
